import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Input, Spinner } from "@heroui/react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import * as Sentry from "@sentry/react";

interface ContactCompanyResult {
  id: number;
  name: string;
}

interface CompanySearchInputProps {
  value: { id: number; name: string } | null;
  onChange: (company: { id: number; name: string } | null) => void;
  isDisabled?: boolean;
}

const CompanySearchInput: React.FC<CompanySearchInputProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const { session } = useSession();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<ContactCompanyResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchCompanies = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/contact/search-companies?query=${encodeURIComponent(query)}`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data: ContactCompanyResult[] = await response.json();
          setResults(data);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: "CompanySearchInput.searchCompanies", query },
        });
        console.error("Fout bij zoeken naar bedrijven:", error);
      } finally {
        setIsSearching(false);
      }
    },
    [session],
  );

  // Debounced trigger
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (searchTerm.length >= 2) {
      timeoutRef.current = setTimeout(() => {
        searchCompanies(searchTerm);
      }, 300);
    } else {
      setResults([]);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [searchTerm, searchCompanies]);

  const handleSelect = (company: ContactCompanyResult) => {
    onChange(company);
    setShowDropdown(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm("");
  };

  if (isDisabled && value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2">
        <span className="text-sm font-medium text-gray-700">{value.name}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2">
          <span className="flex-1 text-sm font-medium">{value.name}</span>
          {!isDisabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-moss-primary hover:underline"
            >
              Wijzig
            </button>
          )}
        </div>
      ) : (
        <Input
          placeholder="Zoek bedrijf..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          size="sm"
          variant="bordered"
          isDisabled={isDisabled}
          startContent={
            <MagnifyingGlass className="h-4 w-4 text-neutral-400" />
          }
          endContent={isSearching && <Spinner size="sm" />}
          classNames={{ input: "text-sm" }}
        />
      )}

      {showDropdown && !value && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {searchTerm.length < 2 && (
            <p className="px-3 py-3 text-center text-sm text-gray-400">
              Typ minstens 2 tekens om te zoeken
            </p>
          )}
          {searchTerm.length >= 2 && !isSearching && results.length === 0 && (
            <p className="px-3 py-3 text-center text-sm text-gray-400">
              Geen bedrijven gevonden voor &quot;{searchTerm}&quot;
            </p>
          )}
          {results.map((company) => (
            <div
              key={company.id}
              onClick={() => handleSelect(company)}
              className="cursor-pointer border-b border-gray-100 px-3 py-2 text-sm hover:bg-moss-primary/10"
            >
              {company.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanySearchInput;
